import * as Device from 'expo-device';
import { Platform } from 'react-native';

/**
 * Verifica se a aplicação está rodando em ambiente web
 */
export const isWeb = Platform.OS === 'web';

/**
 * Verifica se a aplicação está rodando em ambiente iOS
 */
export const isIOS = Platform.OS === 'ios';

/**
 * Verifica se a aplicação está rodando em ambiente Android
 */
export const isAndroid = Platform.OS === 'android';

/**
 * Verifica se a aplicação está rodando em ambiente móvel (iOS ou Android)
 */
export const isMobile = isIOS || isAndroid;

/**
 * Representa informações básicas da plataforma do cliente
 */
export interface ClientPlatformInfo {
  platform: 'ios-native' | 'android-native' | 'web-mobile' | 'web-desktop';
  os: 'ios' | 'android' | 'web';
}

export interface SignupClientMetadata {
  platformInfo: ClientPlatformInfo;
  metadata: Record<string, string>;
}

const mapDeviceType = (deviceType: Device.DeviceType | null | undefined): string | null => {
  switch (deviceType) {
    case Device.DeviceType.PHONE:
      return 'phone';
    case Device.DeviceType.TABLET:
      return 'tablet';
    case Device.DeviceType.DESKTOP:
      return 'desktop';
    case Device.DeviceType.TV:
      return 'tv';
    case Device.DeviceType.UNKNOWN:
      return 'unknown';
    default:
      return null;
  }
};

/**
 * Retorna uma descrição padronizada da plataforma atual do cliente.
 * Utilizado para enviar metadados ao Supabase Auth durante o signup.
 */
export const getClientPlatform = (): ClientPlatformInfo => {
  if (Platform.OS === 'ios') {
    return {
      platform: 'ios-native',
      os: 'ios',
    };
  }

  if (Platform.OS === 'android') {
    return {
      platform: 'android-native',
      os: 'android',
    };
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isMobileUA = /Android|iPhone|iPad|iPod|Windows Phone|BlackBerry|Mobile/i.test(ua);

  return {
    platform: isMobileUA ? 'web-mobile' : 'web-desktop',
    os: 'web',
  };
};

export const getSignupClientMetadata = (appVersion: string): SignupClientMetadata => {
  const platformInfo = getClientPlatform();
  const metadata: Record<string, string> = {
    platform: platformInfo.platform,
    os: platformInfo.os,
    app_version: appVersion,
  };

  if (Platform.OS !== 'web') {
    const nativeDeviceMetadata: Record<string, string | null> = {
      device_model_name: Device.modelName,
      device_manufacturer: Device.manufacturer,
      device_brand: Device.brand,
      device_os_name: Device.osName,
      device_os_version: Device.osVersion,
      device_type: mapDeviceType(Device.deviceType),
      device_is_device: String(Device.isDevice),
    };

    Object.entries(nativeDeviceMetadata).forEach(([key, value]) => {
      if (!value) {
        return;
      }
      metadata[key] = value;
    });
  }

  return {
    platformInfo,
    metadata,
  };
};
